<script setup lang="ts">
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/dropdown-menu'
import type { StrategyFilter } from './strategy_types'

const props = withDefaults(
  defineProps<{
    filter: StrategyFilter
    triggerLabel: string
    clearable?: boolean
    triggerClass?: string
    // Where to portal the menu. The default (body) is right on a plain page, but a
    // consumer that raises itself on a backdrop has to pass its own element: the menu
    // would otherwise render in the body's stacking context, below that backdrop,
    // which leaves it dimmed and swallows every click on an option.
    containerPortal?: string
  }>(),
  { clearable: true, triggerClass: '', containerPortal: undefined }
)

const emit = defineEmits<{
  select: [value: string | string[] | null]
  open: []
}>()

function selectOption(optionLabel: string) {
  const option = props.filter.options.find((candidate) => candidate.label === optionLabel)
  emit('select', option?.value ?? null)
}

function menuOpenChanged(open: boolean) {
  if (open) {
    emit('open')
  }
}
</script>

<template>
  <!-- non-modal: a modal menu marks the rest of the page aria-hidden while it is open,
       which axe reports as a serious aria-hidden-focus violation on these pages -->
  <DropdownMenu :modal="false" @update:open="menuOpenChanged">
    <DropdownMenuTrigger
      :data-testid="`filter-${filter.field}`"
      :disabled="filter.disabled"
      :aria-label="triggerLabel"
      class="inline-flex items-center gap-1 rounded-xs border border-gray-400 bg-white px-3 py-1.5 align-middle text-gray-900 disabled:opacity-50 focus-visible:outline-offset-2"
      :class="triggerClass || 'text-text-lg'"
    >
      {{ filter.valueLabel || filter.emptyText }}
      <!-- 1em rather than a fixed size: the trigger's own type scale is set by the
           consumer (the wizard renders these inside a 30px prompt), and a fixed
           caret next to larger text reads as a stray dot -->
      <span v-if="filter.options.length" class="material-icons-round text-[1em]" aria-hidden="true">
        arrow_drop_down
      </span>
    </DropdownMenuTrigger>

    <!-- The menu is capped to the space actually available on the side it opens to,
         rather than a fixed max-height. A fixed cap taller than the gap below the
         trigger makes the collision handler flip the whole menu above it, which on
         the wizard put an 8-item list on top of the navbar because its triggers sit
         low on the page. -->
    <DropdownMenuContent
      align="start"
      :container-portal="containerPortal"
      :collision-padding="{ top: 64, bottom: 16 }"
      class="max-h-[var(--reka-popper-available-height)] overflow-y-auto bg-white"
    >
      <DropdownMenuRadioGroup
        :model-value="filter.valueLabel"
        @update:model-value="selectOption(String($event))"
      >
        <template v-if="clearable">
          <DropdownMenuRadioItem value="" class="cursor-pointer hover:bg-gray-300">
            {{ filter.emptyText }}
          </DropdownMenuRadioItem>
          <DropdownMenuSeparator class="bg-gray-400" />
        </template>
        <DropdownMenuRadioItem
          v-for="option in filter.options"
          :key="option.label"
          :value="option.label"
          class="cursor-pointer hover:bg-gray-300"
        >
          {{ option.label }}
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
